/**
 * /stem-splitter — the FREE lane: four stems separated in this browser, on the visitor's own
 * graphics card, with no account and no tokens.
 *
 * WHY IT EXISTS (measured, RU, 30 days to 2026-09-18)
 * ──────────────────────────────────────────────────
 * The page had one road and it was a wall: 1011 of 1090 sessions were guests, 552 of them chose a
 * file, 475 were sent to the login page, 6 signed in and NOT ONE ever got a split. The paid lane
 * meanwhile earned 24 charges from 12 people in a month — 0.6 % of all token spend. So the tool's
 * whole audience was arriving with a file in hand and leaving with nothing, and the thing they were
 * being asked to pay for was not what was stopping them.
 *
 * WHAT THE STUDIO STILL BUYS — measured, not assumed (sepbench/dfm, MUSDB18 clips, SI-SDR medians,
 * paired per clip; `score4.py`). The paid lane is NOT htdemucs_ft: since 2026-08-19 its model is
 * `kim_hybrid` (roformer vocals + htdemucs on the leftover), so the comparison that matters is:
 *
 *                       drums   bass   other  vocals   minus (mix − vocals)
 *   this browser lane    9.00   8.43    3.91    8.30   13.21
 *   the studio          9.45   8.62    6.00   11.23   16.01
 *   paired difference   +0.46  +2.35*  +1.72   +3.82   +2.73  (50/50 clips better on the minus)
 *                                  * ±3.14 — not significant
 *
 * i.e. drums and bass come out in the SAME class for free; what the studio really sells is the
 * vocal and the backing track. That is what the page says, in those words, with those numbers —
 * a free lane that quietly did the same job would make the paid one a trick, and a free lane sold
 * as worse than it is would be the other kind of lie.
 *
 * HOW IT RUNS. `MusionStemSeparator` (HTDemucs by Meta in MUSION's ONNX export) in a worker on a
 * WebGPU provider that has passed a known-answer test. Stems come back as AudioBuffers, are encoded
 * to WAV blobs and handed to the page's EXISTING result block through `window.__stemShowLocal` —
 * player, per-stem download, ZIP, ready mixes and the mixer bridge are the paid lane's, unchanged.
 * A second copy of that UI is the one thing this lane must not grow.
 *
 * ⚠️ The unit of the daily cap is the FILE (begin(slug, fileKey)), and ONLY this lane calls
 * begin() — the token path must never touch it, or a person who bought tokens would hit a free
 * wall. Guarded from both sides: ToolDailyCapMirrorTest ($mixedFreePaidAllowed) and
 * StemSplitterLaneSeparationTest.
 */
(function () {
  'use strict';

  var cfg = window.__stemSplit;
  var goBtn = document.getElementById('stemBrowserGo');
  if (!cfg || !goBtn) return;                      // the paid panel alone (e.g. embedded elsewhere)

  var T = cfg.strings || {};
  var SLUG = 'stem-splitter';
  var STEMS = ['drums', 'bass', 'other', 'vocals'];

  /* How long a track THIS device can take — measured, not rounded.
     Probe of 2026-09-19 (tests/playwright/stem-splitter-browser-memory-probe.mjs, Chrome, working
     set of the whole process tree): peak 2201 MB at 1 minute, 4253 MB at 10 — a fixed ≈1.4 GB for
     the model (on the GPU and again in wasm, plus the ORT arenas) and ≈230 MB for every further
     minute of audio, because the page holds the decoded source, four full-length output buffers
     and then four WAV blobs at once.
     So ten minutes is fine on the machine it was measured on and fatal on the 4 GB device that
     support() still admits — and it would be fatal in the worst way, with the tab dying and no
     error to show. One minute of audio per gigabyte of reported memory keeps the variable part
     inside the same quarter-gigabyte-per-GB budget the shared uploader already uses for files.
     ⚠️ The gigabyte figure comes from `window.__memBudget` and not from
     `navigator.deviceMemory || 4`: that API is absent in all of WebKit, so a local fallback is a
     budget decided in secret and disagreeing with every other copy. (Moot in practice here — the
     engine's support() refuses WebKit outright — but a number with an owner has an owner.) */
  var CEIL_SEC = +cfg.browserMaxSec || 600;
  var MAX_SEC = (function () {
    var B = window.__memBudget;
    var gb = B ? B.gb() : 4;
    return Math.max(180, Math.min(CEIL_SEC, Math.round(gb * 60)));
  })();

  var $ = function (id) { return document.getElementById(id); };
  var els = {
    drop: $('stemDrop'), go: goBtn, off: $('stemBrowserOff'), lane: $('stemBrowserLane'),
    prog: $('stemBrowserProgress'), bar: $('stemBrowserBar'), stage: $('stemBrowserStage'),
    cancel: $('stemBrowserCancel'), studio: $('stemGo')
  };

  var file = null, fileKey = null, support = null, ctl = null, busy = false, runGen = 0;
  var localUrls = [];

  // The promise on screen becomes this device's, not the ceiling's.
  (function () {
    var n = document.querySelector('[data-ss-max-min]');
    if (n) n.textContent = String(Math.round(MAX_SEC / 60));
  })();

  function feature(a, d) { try { window.__aeFeature && window.__aeFeature(a, d || {}); } catch (_) {} }
  function toast(m, t) { if (window.__toast) window.__toast(m, t); }

  /* Why the engine cannot run HERE, in the visitor's words. The reasons come from the engine's own
     support() — one owner for the question, so the page can never offer a button that cannot work. */
  var WHY = {
    no_webgpu: 'why_no_webgpu', software_gpu: 'why_no_webgpu', no_worker: 'why_no_webgpu',
    not_wired: 'why_no_webgpu', webkit: 'why_webkit', low_memory: 'why_low_memory',
    gpu_rejected: 'why_gpu_rejected', gpu_output_corrupt: 'why_gpu_rejected',
    ep_ladder_exhausted: 'why_gpu_rejected'
  };

  function paint() {
    var ok = !!(support && support.ok);
    if (els.lane) els.lane.classList.toggle('is-off', !ok);
    els.go.hidden = !ok;
    els.go.disabled = !ok || !file || busy;
    if (els.off) {
      if (ok) { els.off.hidden = true; }
      else {
        var why = T[WHY[(support && support.reason) || 'no_webgpu'] || 'why_no_webgpu'] || '';
        els.off.textContent = String(T.browser_off || '').replace(':why', why);
        els.off.hidden = !els.off.textContent;
      }
    }
    // The studio is the primary button only when the free road is closed here.
    if (els.studio) {
      els.studio.classList.toggle('lg-btn-primary', !ok);
    }
  }

  function setProgress(p, text) {
    if (!els.prog) return;
    els.prog.hidden = false;
    if (els.bar) els.bar.style.width = Math.round(Math.max(0, Math.min(1, p)) * 100) + '%';
    if (els.stage && text !== undefined) els.stage.textContent = text;
  }
  function hideProgress() {
    if (els.prog) els.prog.hidden = true;
    if (els.bar) els.bar.style.width = '0%';
    if (els.cancel) els.cancel.hidden = true;
  }
  function fmtEta(sec) {
    var min = Math.max(1, Math.round(sec / 60));
    try {
      return new Intl.NumberFormat(window.__LOCALE || 'en', { style: 'unit', unit: 'minute', unitDisplay: 'short' }).format(min);
    } catch (_) { return min + ' min'; }
  }

  /* Bars for the result strip, measured from the audio we just produced. The paid lane gets them
     from the job's meta; here the audio is in hand, so they are real rather than absent. */
  function peaks(buf, n) {
    n = n || 96;
    var d = buf.getChannelData(0), step = Math.max(1, Math.floor(d.length / n)), out = [], max = 0, i, j;
    for (i = 0; i < n; i++) {
      var s = 0, c = 0;
      for (j = i * step; j < Math.min((i + 1) * step, d.length); j += 4) { s += d[j] * d[j]; c++; }
      var v = Math.sqrt(s / Math.max(c, 1));
      out.push(v); if (v > max) max = v;
    }
    return out.map(function (v) { return Math.round(Math.min(100, (v / (max || 1)) * 100)); });
  }

  /* Level of a stem, dBFS RMS over both channels — what «almost silent» is decided on. */
  function rmsDb(buf) {
    var s = 0, n = 0;
    for (var c = 0; c < buf.numberOfChannels; c++) {
      var d = buf.getChannelData(c);
      for (var i = 0; i < d.length; i += 2) { s += d[i] * d[i]; n++; }
    }
    return 10 * Math.log10(Math.max(s / Math.max(n, 1), 1e-12));
  }

  function revokeLocal() {
    localUrls.forEach(function (u) { try { URL.revokeObjectURL(u); } catch (_) {} });
    localUrls = [];
  }

  function keyOf(f) { return f ? [f.name, f.size, f.lastModified].join(':') : null; }

  /* ── the run ──────────────────────────────────────────────────────────────────────────── */
  async function run() {
    var M = window.MusionStemSeparator;
    if (!file || !M || busy) return;
    var gen = ++runGen, t0 = Date.now(), tReady = 0, tFirst = 0, segs = 0;
    busy = true; paint();
    if (els.cancel) els.cancel.hidden = false;
    setProgress(0.02, T.browser_stage_decode || '');
    var unit = null, buffer = null, myCtl = null;
    try {
      buffer = await window.__decodeAudio(null, file);
      if (gen !== runGen) return;
      if (!buffer || !buffer.duration) throw new Error('decode');
      if (buffer.duration > MAX_SEC) {
        toast(String(T.browser_too_long || '').replace(':min', Math.round(MAX_SEC / 60)), 'warning');
        feature('browser_too_long', { sec: Math.round(buffer.duration) });
        return;
      }
      // The unit is spent per FILE and only by a delivered result; the wall (if any) is shown by
      // the shared block, which is why begin() comes before the long wait, not after it.
      unit = window.__toolLimit ? window.__toolLimit.begin(SLUG, fileKey || 'unknown') : null;
      if (window.__toolLimit && !unit) return;

      myCtl = new AbortController(); ctl = myCtl;
      var out = await M.split(buffer, {
        slug: SLUG, stems: STEMS, signal: myCtl.signal,
        onProgress: function (p, st, d) {
          if (gen !== runGen) return;
          if (!d) { setProgress(0.02 + p * 0.7, p >= 0.3 ? (T.browser_stage_check || '') : (T.browser_stage_model || '')); return; }
          if (!tReady) { tReady = Date.now() - t0; tFirst = Date.now(); }
          segs = d.of;
          var line = String(T.browser_stage_split || '').replace(':done', d.done).replace(':of', d.of);
          // The record keeps its bass to one side: the engine separates it on passes of its own,
          // and the count above is of all of them. Said in words, or «3× longer» has no reason.
          if (d.route && T.browser_stage_wide) line = T.browser_stage_wide + ' ' + line;
          // measured on THIS device, from the second segment on (the first still carries warm-up)
          var eta = d.done >= 2 ? (Date.now() - tFirst) / (d.done - 1) * (d.of - d.done) / 1000 : 0;
          setProgress(0.02 + p * 0.7, eta > 45 ? line + ' · ≈ ' + fmtEta(eta) : line);
        }
      });
      if (gen !== runGen) return;
      var info = (M.info && M.info()) || {};
      var place = (M.lastPlace && M.lastPlace()) || {};
      // Hand the model's memory back before encoding: only terminating its worker returns it.
      try { M.release(); } catch (_) {}

      setProgress(0.78, T.browser_stage_files || '');
      var stems = [], loudest = -Infinity;
      for (var i = 0; i < STEMS.length; i++) {
        var k = STEMS[i], b = out[k];
        if (!b) continue;
        var lv = rmsDb(b);
        if (lv > loudest) loudest = lv;
        stems.push({ stem: k, url: URL.createObjectURL(window.AudioPost.encodeWav(b)), format: 'wav', wave: peaks(b), rms_db: lv });
        out[k] = null;                              // let each buffer go as soon as its file exists
        setProgress(0.78 + 0.2 * ((i + 1) / STEMS.length));
        await new Promise(function (r) { setTimeout(r, 0); });
      }
      // «Almost silent» by the rule the server lane uses (StemQuiet): this far under the loudest
      // stem of the same split. A stem nobody will hear is named, not handed over as music.
      var rel = +cfg.quietRelDb || -40;
      stems.forEach(function (s) { s.quiet = s.rms_db <= loudest + rel; });
      revokeLocal();
      localUrls = stems.map(function (s) { return s.url; });
      hideProgress();
      if (typeof window.__stemShowLocal === 'function') window.__stemShowLocal(stems);
      if (unit) { unit.commit(); unit = null; }
      feature('browser_split_done', {
        sec: Math.round(buffer.duration), ms: Date.now() - t0, ms_ready: tReady, segs: segs,
        ep: info.provider || null, seg_ms: info.segMs || 0,
        ext: (file.name || '').split('.').pop().toLowerCase().slice(0, 5),
        // where the record keeps its low end, which way the bass went, and whether any stem came out empty
        low_side: place.low_side != null ? place.low_side : null, route: place.route || null,
        quiet: stems.filter(function (s) { return s.quiet; }).map(function (s) { return s.stem; }).join(',') || null
      });
      try { window.__aeToolComplete && window.__aeToolComplete({ lane: 'browser', sec: Math.round(buffer.duration), ms: Date.now() - t0, ep: info.provider || null }); } catch (_) {}
    } catch (e) {
      if (gen !== runGen) return;
      hideProgress();
      var cancelled = e && e.name === 'AbortError';
      if (cancelled) { feature('browser_cancelled', { ms: Date.now() - t0 }); return; }
      var msg = String((e && e.message) || e);
      try { console.error('[stem-splitter browser]', e); } catch (_) {}
      try { window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'browser_split_failed', code: (e && e.code) || null, err: msg.slice(0, 120) }); } catch (_) {}
      toast(T.browser_failed || T.error || 'Error', 'error');
      // A provider that answered WRONG (or never answered) closes this road for this device, and
      // the studio is the honest next step — the same rule /drum-remover uses.
      if (e && (e.code === 'gpu_output_corrupt' || e.code === 'ep_ladder_exhausted' || e.code === 'gpu_rejected')) {
        support = { ok: false, reason: e.code };
        paint();
      }
    } finally {
      if (unit) unit.abort();
      if (ctl === myCtl) ctl = null;          // never the controller of a run that started after this one
      busy = false; paint();
      if (els.cancel) els.cancel.hidden = true;
    }
  }

  /* ── wiring ───────────────────────────────────────────────────────────────────────────── */
  els.go.addEventListener('click', function () { run(); });
  if (els.cancel) els.cancel.addEventListener('click', function () { if (ctl) { try { ctl.abort(); } catch (_) {} } });

  if (els.drop) {
    var onFile = function (f) {
      if (!f) return;
      file = f; fileKey = keyOf(f);          // stamped ONCE per file: a repeat run today is free
      revokeLocal();
      paint();
    };
    if (typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(els.drop, function (f) { onFile(f); });
    else els.drop.addEventListener('tool-uploader:file', function (e) { onFile(e && e.detail && e.detail.file); });
    els.drop.addEventListener('tool-uploader:clear', function () { file = null; fileKey = null; revokeLocal(); paint(); });
  }

  /* Whether the engine can run here is a fact about the DEVICE, not about the file: ask once, at
     load, so a closed road is on screen before anyone chooses anything. The facade is a deferred
     script with a second source — give it its chance before saying «not wired». */
  (function probe(tries) {
    var M = window.MusionStemSeparator;
    if (!M || typeof M.support !== 'function') {
      if ((tries || 0) < 40) { setTimeout(function () { probe((tries || 0) + 1); }, 150); return; }
      support = { ok: false, reason: 'not_wired' }; paint(); return;
    }
    M.support().then(function (s) {
      support = s || { ok: false, reason: 'no_webgpu' };
      feature('browser_lane_support', {
        ok: support.ok ? 1 : 0, why: support.ok ? null : support.reason,
        gpu: (s && s.adapter) ? [s.adapter.vendor, s.adapter.architecture].join('/') : null
      });
      paint();
    }, function () { support = { ok: false, reason: 'no_webgpu' }; paint(); });
  })(0);

  paint();
  window.__stemBrowserLane = {                      // read by the e2e; never by the paid path
    support: function () { return support; },
    canRun: function () { return !!(support && support.ok); }
  };
})();
